import { useCallback } from 'react';
import { useNotify } from '../../../hooks';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import { useReportErrorToBugsnag } from '../../../util/bugsnagReporting';
import { Folder, ModelUtils, rootFolder } from '../models';
import { getTemplatingItemsInFolder } from './getTemplatingItemsInFolder';
import { useInvalidateTemplatingItems } from './useInvalidateTemplatingItems';

export const useMoveTemplatingFolder = () => {
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const reportError = useReportErrorToBugsnag();
  const invalidateTemplatingItems = useInvalidateTemplatingItems();

  const moveTemplatingFolder = useCallback(
    async (sourceFolder: Folder, destination: Folder = rootFolder) => {
      const movedFolder = ModelUtils.moveToFolder(sourceFolder, destination);
      if (ModelUtils.hasSamePath(sourceFolder, movedFolder)) {
        return;
      }
      // Prevent dropping a folder into itself or any descendant.
      if (ModelUtils.isChildOf(sourceFolder, movedFolder)) {
        return;
      }

      try {
        const templatingItems = await getTemplatingItemsInFolder(
          apiClient,
          sourceFolder,
        );
        for (const templatingItem of templatingItems) {
          await apiClient.request('PATCH /v1/ai-templates/:id', {
            type: templatingItem.type,
            id: templatingItem.id,
            path: ModelUtils.reparent(templatingItem, sourceFolder, movedFolder)
              .path,
          });
        }

        invalidateTemplatingItems(
          sourceFolder,
          movedFolder,
          ModelUtils.getParentFolder(sourceFolder),
          destination,
        );
        notify('Folder moved', 'success');
      } catch (error) {
        reportError(error);
        notify('Failed to move folder', 'error');
      }
    },
    [apiClient, invalidateTemplatingItems, notify, reportError],
  );

  return {
    moveTemplatingFolder,
  };
};
